import type { TFunction } from 'i18next';
import type { DisruptionOverviewRow } from '@/lib/disruptions/types';
import {
  BOARD_BADGE_TONE,
  boardFlights,
  isOpen,
  rowBadge,
  rowFigure,
} from '@/routes/disruptions/board-model';
import { disruptionEntry } from '@/routes/disruptions/disruption-entry';
import type { ActionTone } from '@/routes/disruptions/operar/views/cancelled/action-tone';
import type { IOpenBoard } from '@/routes/disruptions/use-open-board';
import type { DashboardCounters } from './types';

// «Resumen»'s list of WHAT needs the operator now (dashboard.tsx draws it). Pure — the
// route renders, this derives; tests pin it. Copy is addressed by i18n KEY (`admin`, and
// `disruptions:` for the board's own words), so the rows say what /disruptions says.

/** How many «waits for your decision» rows the list names before it stops. */
const MAX_DECISIONS = 5;

/** One row of «what needs you». */
export interface INeed {
  id: string;
  kind: 'decision' | 'unresolved' | 'queue';
  /** A disruption's — its detail (the flight) is still being read. */
  pending?: boolean;
  title: string;
  /** The title is a flight code (mono). */
  mono: boolean;
  sub?: string;
  /** The flight's scheduled departure — WHEN it is («Hoy 14:35», flight-when.tsx). */
  when?: { departureAt: string; origin?: string };
  chip?: { text: string; className: string };
  why: string;
  /** How many of it the row stands for — 1 for a disruption, the queue's depth for a queue. */
  count: number;
  /** The why is a warning (rose, with its icon). */
  alert: boolean;
  action: string;
  tone: ActionTone;
  to: string;
  state?: unknown;
}

/**
 * The list of WHAT needs the operator now, most urgent first: the recent open
 * disruptions waiting for a decision (newest first), then the one with the most
 * passengers unresolved — the same two rules, in the same order, as «Lo que sigue»
 * (`nextOnBoard`) — then the stuck reservations, when the counters say there are any.
 * Pure — tests pin it.
 */
export function needsYou(
  board: Pick<IOpenBoard, 'groups' | 'casesByUrn' | 'pendingUrns'>,
  counters: Pick<DashboardCounters, 'stuckReservations'> | undefined,
  t: TFunction<'admin'>,
): INeed[] {
  const recent = [...board.groups.today, ...board.groups.yesterday].filter(isOpen);
  const disruption = (
    row: DisruptionOverviewRow,
  ): Pick<INeed, 'pending' | 'title' | 'mono' | 'sub' | 'when' | 'chip' | 'to' | 'state'> => {
    const flight = boardFlights(row, board.casesByUrn.get(row.urn))[0];
    const badge = rowBadge(row);
    const entry = disruptionEntry(row.urn);
    return {
      pending: !flight && board.pendingUrns.has(row.urn),
      title: flight?.code ?? row.title,
      mono: !!flight,
      ...(flight?.origin && flight.destination
        ? {
            sub: t('disruptions:board.queue.route', {
              origin: flight.origin,
              destination: flight.destination,
            }),
          }
        : {}),
      ...(flight?.departureAt
        ? { when: { departureAt: flight.departureAt, ...(flight.origin ? { origin: flight.origin } : {}) } }
        : {}),
      chip: { text: t(`disruptions:${badge.key}`, badge.params ?? {}), className: BOARD_BADGE_TONE[badge.tone] },
      to: entry.to,
      state: entry.state,
    };
  };

  const out: INeed[] = [];
  const decisions = recent.filter((r) => !!r.flightStatus?.outOfPolicy).slice(0, MAX_DECISIONS);
  for (const row of decisions) {
    out.push({
      id: `decision-${row.urn}`,
      kind: 'decision',
      ...disruption(row),
      why: t('disruptions:board.next.decision'),
      count: 1,
      alert: true,
      action: t('disruptions:board.queue.decide'),
      tone: 'alert',
    });
  }

  let top: { row: DisruptionOverviewRow; n: number } | null = null;
  for (const row of recent) {
    if (decisions.includes(row)) continue;
    const figure = rowFigure(row);
    if (figure?.kind !== 'unresolved' || figure.n === null || figure.n <= 0) continue;
    if (!top || figure.n > top.n) top = { row, n: figure.n };
  }
  if (top) {
    out.push({
      id: `unresolved-${top.row.urn}`,
      kind: 'unresolved',
      ...disruption(top.row),
      why: t('disruptions:board.next.unresolved', { count: top.n, n: top.n.toLocaleString() }),
      count: 1,
      alert: false,
      action: t('disruptions:board.queue.operate'),
      tone: 'act',
    });
  }

  // Manual review is not listed (PO 2026-11-03): its own screen is its queue.
  const queue = (id: 'stuckReservations', n: number | undefined) => {
    if (typeof n !== 'number' || n <= 0) return;
    out.push({
      id,
      kind: 'queue',
      title: t(`dashboard.needs.${id}.title`),
      mono: false,
      chip: {
        text: t(`dashboard.needs.${id}.count`, { count: n }),
        className: 'bg-muted text-muted-foreground',
      },
      why: t(`dashboard.needs.${id}.why`),
      count: n,
      alert: false,
      action: t('dashboard.needs.review'),
      tone: 'alert',
      to: '/manual-review',
    });
  };
  queue('stuckReservations', counters?.stuckReservations);

  // The first is what comes next — the filled action (Operar's «Lo que sigue»).
  if (out[0]) out[0] = { ...out[0], tone: 'next' };
  return out;
}

/** One phrase of the band's headline: a kind of need and how many of it. */
export interface INeedsPart {
  key: 'decision' | 'unresolved' | 'stuckReservations';
  count: number;
}

/**
 * What the headline names, in list order — «2 cosas te necesitan» said nothing about
 * WHAT (PO 2026-11-03), so each kind is counted in its own words.
 */
export function needsParts(items: readonly INeed[]): INeedsPart[] {
  const parts: INeedsPart[] = [];
  for (const item of items) {
    const key: INeedsPart['key'] = item.kind === 'queue' ? 'stuckReservations' : item.kind;
    const held = parts.find((part) => part.key === key);
    if (held) held.count += item.count;
    else parts.push({ key, count: item.count });
  }
  return parts;
}
